import { Button } from "@/components/ui/baseUi/button"
import { Chip } from "@/components/ui/baseUi/chip"
import { ChipGroup } from "@/components/ui/baseUi/chip-groupe"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { NoteType } from "@/modules/sessions/sessionDetails/blocks/noteBlock/noteTypes"
import {
  getCoachNoteFormSchema,
  type CoachNoteFormikType,
  type CoachNoteFormType,
} from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { word } from "@modules/core/utils/i18n"
import { HURichTextInput } from "@modules/ui/components/richText/huRichTextInput"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { LoaderCircle, XIcon } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

type NoteForCoachesFormProps = {
  updatedNote?: CoachNoteFormikType
  updatedNoteIndex: number
  onValidate: (note: CoachNoteFormikType) => Promise<void>
  className?: string
} & React.ComponentProps<"div">

export function NoteForCoachesForm({
  updatedNote,
  updatedNoteIndex,
  onValidate,
  className,
  ...props
}: NoteForCoachesFormProps) {
  const onCloseSidebarFnRef = useRef<() => void>(() => undefined)
  const onCloseNoteForCoachesFormFnRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true))
  const { updateSidebarStackOnTop, hideSidebarStackOnTop, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()
  // Subscribe to language changes so a locale switch re-renders this form and rebuilds the schema
  // below with freshly translated validation messages.
  useLanguage()

  const coachNoteFormSchema = getCoachNoteFormSchema()

  const form = useAppForm({
    defaultValues:
      updatedNote ??
      ({
        id: null,
        note: "",
        noteType: NoteType.LESSON_PLAN,
        durationInMin: null,
      } satisfies CoachNoteFormType),
    validators: {
      onSubmit: coachNoteFormSchema,
      onBlur: coachNoteFormSchema,
      onChange: coachNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onValidate(value)
    },
  })

  const onCloseNoteForCoachesForm = useCallback(
    async (shouldHideSidebar: boolean) => {
      // Close the sidebar top stack and hide the sidebar if the shouldHideSidebar is true
      const closeStack = async () => {
        hideSidebarStackOnTop()
        if (shouldHideSidebar) {
          // Wait for the sidebar top stack to be hidden before hiding the sidebar
          await new Promise((resolve) => setTimeout(resolve, 50))
          hideSidebar()
        }
      }

      // If there is no updated note, close the sidebar top stack and return false
      if (!updatedNote) {
        await closeStack()
        return Promise.resolve(false)
      } else {
        // If there is an updated note and the form is dirty, show the confirm popup
        if (form.state.isDirty) {
          const confirm = await new Promise<boolean>((resolve) => {
            confirmPopup.show({
              title: word("program.note.closeMessage.title"),
              message: word("program.note.closeMessage.message"),
              accept: async () => {
                await closeStack()
                resolve(false)
              },
              cancel: () => undefined,
              footerProps: {
                confirmText: word("global.ok"),
              },
            })
          })
          return confirm
        } else {
          // If there is an updated note and the form is not dirty, close the sidebar top stack and return false
          await closeStack()
          return Promise.resolve(false)
        }
      }
    },
    [updatedNote, confirmPopup, hideSidebarStackOnTop, hideSidebar, form],
  )

  // Defines the custom header buttons for the sidebar top stack
  const customHeaderButtons = [
    <Button
      key="close-note-for-coaches-form"
      variant="outline"
      size="iconMd"
      onClick={() => {
        onCloseSidebarFnRef.current()
      }}
    >
      <XIcon color="currentColor" size={16} />
    </Button>,
  ]

  useEffect(() => {
    // Add the custom header buttons and the left button click handler to the sidebar top stack
    updateSidebarStackOnTop({
      customHeaderButtons,
      sidebarLeftButtonClick: () => onCloseNoteForCoachesFormFnRef.current(),
    })
  }, [])

  useEffect(() => {
    // Set the onCloseSidebarFnRef and onCloseNoteForCoachesFormFnRef to the onCloseNoteForCoachesForm function
    onCloseSidebarFnRef.current = () => {
      void onCloseNoteForCoachesForm(true)
    }
    onCloseNoteForCoachesFormFnRef.current = () => {
      return onCloseNoteForCoachesForm(false)
    }
  }, [onCloseNoteForCoachesForm])

  const handleSubmit = async () => {
    // Validate every field with the "submit" cause so errors are surfaced (fields get marked as touched)
    await form.validateAllFields("submit")
    if (!form.state.isValid) return
    await form.handleSubmit()
  }

  return (
    <div className={cn("flex flex-1 flex-col justify-between gap-4 px-5 pt-18 pb-6", className)} {...props}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-body uppercase">
            {`(${String(updatedNoteIndex + 1)}) ${word("program.planning.create.exercise.block_plural", { count: updatedNoteIndex })}`}
          </Text>

          <Text fontStyle="TL">{word("sessionForm.blocks.add_note_for_coaches.title")}</Text>
          <Text fontStyle="BS">{word("sessionForm.blocks.add_note_for_coaches.description")}</Text>
        </div>

        {/* Note type input */}
        <div className="card mb-0 p-3">
          <Text fontStyle="LS" className="uppercase">
            {word("sessionForm.blocks.add_note_for_coaches.note_type")}
            <Text fontStyle="LL" className="text-textError">
              {" *"}
            </Text>
          </Text>

          <form.AppField
            name="noteType"
            validators={{ onChange: coachNoteFormSchema.shape.noteType, onBlur: coachNoteFormSchema.shape.noteType }}
          >
            {(field) => (
              <>
                <ChipGroup
                  spacing={2}
                  variant="emphasisLow"
                  className="mt-2 w-full flex-wrap"
                  value={field.state.value ? [field.state.value] : []}
                >
                  {Object.values(NoteType).map((noteType) => (
                    <Chip
                      value={noteType}
                      key={noteType}
                      variant="emphasisLow"
                      size="md"
                      aria-pressed={field.state.value === noteType}
                      onClick={() => {
                        field.handleChange(noteType)
                      }}
                    >
                      {word(`program.note.noteType.${noteType}`)}
                    </Chip>
                  ))}
                </ChipGroup>
                <field.Message />
              </>
            )}
          </form.AppField>
        </div>

        {/* Duration of note*/}
        <div className="card mb-0 p-3">
          <form.AppField name="durationInMin">
            {(field) => (
              <field.Field>
                <field.Label>{word("sessionForm.blocks.add_note_for_coaches.duration.label")}</field.Label>
                <field.Description>
                  {word("sessionForm.blocks.add_note_for_coaches.duration.description")}
                </field.Description>
                <field.Content className="flex-row items-center gap-2">
                  <Input
                    id="durationInMin"
                    className="bg-surface-background pr-4 text-right"
                    type="number"
                    inputMode="numeric"
                    step={1}
                    min={0}
                    max={600}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      const rawValue = e.target.value
                      if (rawValue === "") {
                        field.handleChange(0)
                        return
                      }
                      const parsedValue = parseInt(rawValue)
                      if (Number.isNaN(parsedValue)) field.handleChange(0)
                      else if (parsedValue > 600) field.handleChange(600)
                      else if (parsedValue < 0) field.handleChange(0)
                      else field.handleChange(parsedValue)
                    }}
                  />
                  <Text fontStyle="TM" className="flex items-center">
                    {word("global.minute.shortLabel", {
                      count: field.state.value ?? 1,
                    })}
                  </Text>
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        {/* Note content input */}
        <div className="card mb-0 p-3">
          <form.AppField
            name="note"
            validators={{ onChange: coachNoteFormSchema.shape.note, onBlur: coachNoteFormSchema.shape.note }}
          >
            {(field) => (
              <HURichTextInput
                value={formatTextToRichText(field.state.value ?? "")}
                onChange={(value) => {
                  // HURichTextInput always emits an HTML string; its props intersect ComponentProps<"div">
                  // which widens onChange's param, so narrow it back before handing it to the field.
                  if (typeof value === "string") field.handleChange(value)
                }}
                label={word("sessionForm.blocks.add_note_for_coaches.note_content")}
                isRequiredInput
                maxLength={65000}
                paragrapheFontStyle="BM"
                isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
                error={<field.Message />}
              />
            )}
          </form.AppField>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between">
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
              onClick={() => {
                void handleSubmit()
              }}
            >
              {isSubmitting ? <LoaderCircle className="animate-spin" /> : null}
              {updatedNote ? word("global.save") : word("global.check")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
